import { useEffect } from 'react'
import { BrowserRouter, Routes, Route, useNavigate } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { DashboardLayout } from '@/layouts/DashboardLayout'
import { AuthGuard } from '@/components/shared/AuthGuard'
import { LoginPage } from '@/pages/LoginPage'
import { NotFoundPage } from '@/pages/NotFoundPage'
import { Loader } from '@/components/shared/Loader'
import { ROUTES } from '@/constants'
import { getAuth0AuthorizationParams } from '@/lib/auth0Config'

// ─── Auth0 callback handler ───────────────────────────────────────────────────
// Mounted at /callback — receives the Auth0 redirect and sends the user
// to the root ( / ) where the protected dashboard lives.

function Auth0CallbackHandler() {
  const { isLoading, isAuthenticated, loginWithRedirect, error } = useAuth0()
  const navigate = useNavigate()

  useEffect(() => {
    if (isLoading) return
    if (error) return

    if (isAuthenticated) {
      navigate(ROUTES.HOME, { replace: true })
    } else {
      // Only initiate login when there is no Auth0 code in the URL.
      // The presence of ?code= means Auth0-react is still exchanging the
      // authorization code (PKCE) for tokens.
      if (new URLSearchParams(window.location.search).has('code')) return
      void loginWithRedirect({
        appState: { returnTo: ROUTES.HOME },
        authorizationParams: getAuth0AuthorizationParams(),
      })
    }
  }, [isLoading, isAuthenticated, loginWithRedirect, navigate, error])

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-bg-secondary">
        <p className="text-sm font-medium text-red-600">Error al iniciar sesión</p>
        <p className="max-w-xs text-center text-xs text-text-tertiary">{error.message}</p>
        <button
          className="mt-2 text-xs text-brand-600 underline"
          onClick={() =>
            void loginWithRedirect({
              appState: { returnTo: ROUTES.HOME },
              authorizationParams: getAuth0AuthorizationParams(),
            })
          }
        >
          Reintentar
        </button>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-secondary">
      <Loader message="Iniciando sesión..." />
    </div>
  )
}

// ─── Router ───────────────────────────────────────────────────────────────────

export function AppRouter() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Auth0 redirect target — must be a real URL path */}
        <Route path="/callback" element={<Auth0CallbackHandler />} />

        {/* Manual login entry-point */}
        <Route path={ROUTES.LOGIN} element={<LoginPage />} />

        {/* Protected app — always served at root; URL never changes during navigation */}
        <Route element={<AuthGuard />}>
          <Route path={ROUTES.HOME} element={<DashboardLayout />} />
        </Route>

        <Route path={ROUTES.NOT_FOUND} element={<NotFoundPage />} />
      </Routes>
    </BrowserRouter>
  )
}
